/**
 * Tester profile types
 * Represents testers who can claim and complete test jobs
 */

import type { JobStatus, JobModifier } from '../job/job-status.js';
import type { ScheduleTimezone } from '../schedule/schedule.types.js';
import type { BreakRequestStatus } from '../contracts/break-request.contract.js';
import type { TimeOffRequestStatus } from '../contracts/time-off-request.contract.js';

/**
 * Tester bio/personality information
 */
export interface TesterBio {
  readonly favoriteIceCream?: string;
  readonly favoriteMovies: string[];
  readonly favoriteApps: string[];
  readonly hobbies: string[];
  readonly funFact?: string;
}

/**
 * Device types a tester can test on
 */
export const TESTER_DEVICES = ['ios', 'android', 'pc', 'mac'] as const;
export type TesterDevice = typeof TESTER_DEVICES[number];

/**
 * Languages a tester can communicate in
 */
export const TESTER_LANGUAGES = ['english', 'spanish'] as const;
export type TesterLanguage = typeof TESTER_LANGUAGES[number];

/**
 * Tester profile status (#4272).
 *
 * - `pending`     — Submitted an application; awaiting admin review.
 * - `active`      — Approved tester; can claim jobs.
 * - `rejected`    — Application denied. Never worked a job. Allowed to self-reapply
 *                   (existing row is reused; status moves back to `pending`).
 * - `deactivated` — Was once `active` and has job history; status was revoked.
 *                   Cannot self-reapply — only an admin can reactivate them.
 * - `banned`      — Barred from being a tester. Hard-stop in the apply handler; even
 *                   self-reapply is refused.
 *
 * Every transition between these states is recorded in `tester_status_transitions`
 * via `TesterStatusService`. The legal-transition matrix is enforced in code
 * (see `tester-state-transitions.ts` in this package).
 */
export type TesterStatus = 'pending' | 'active' | 'rejected' | 'deactivated' | 'banned';

export const TESTER_STATUSES = ['pending', 'active', 'rejected', 'deactivated', 'banned'] as const;

/**
 * Append-only entry in the tester status transition log (#4272). Written
 * by `TesterStatusService` on every status change. `fromStatus` is `null`
 * only for the initial application row.
 */
export interface TesterStatusTransition {
  readonly id: string;
  readonly testerProfileId: string;
  readonly fromStatus: TesterStatus | null;
  readonly toStatus: TesterStatus;
  /** Null when the transition was triggered by the tester themselves. */
  readonly actorUserId: string | null;
  readonly reason: string | null;
  readonly createdAt: string;
}

/**
 * Tester self-set availability — the value persisted on `tester_profiles.availability`.
 *
 * - `online`   — Available and active. Default for new profiles. Sweep promotes
 *                online testers to `away` after `TESTER_AUTO_AWAY_MINUTES` of
 *                inactivity, and to `offline` after `TESTER_AUTO_OFFLINE_MINUTES`.
 * - `away`     — Around but not actively engaged. Self-set OR auto-promoted from
 *                online. Sweep further demotes to `offline` after the auto-offline
 *                threshold.
 * - `offline`  — Manually disengaged. Sweep ignores; the tester opted out and
 *                must re-engage explicitly.
 * - `on_break` — Approved break window covers "now" (#3774). Written by the
 *                presence sweep when an approved-break override
 *                (`tester_schedule_overrides.override_type='remove'` +
 *                `reason='lunch'`) intersects the current half-hour, and
 *                cleared back to `online` once the window ends. Excluded
 *                from auto-Away/auto-Offline demotions — the sweep only
 *                considers `online` and `away` rows for those checks.
 * - `invisible` — Admin-only (#4427). Hidden from the presence list AND
 *                blocked from claiming jobs. New admin accounts without a
 *                tester profile get auto-provisioned at `invisible` so they
 *                stay out of the queue unless they explicitly flip
 *                themselves visible. Non-admins cannot set this value —
 *                rejected at the PATCH /tester/profile boundary.
 *
 * Note: "Busy", "Inactive", and "Missing" are *derived* presence states (computed
 * from `activeJob`, `lastActiveAt`, and `scheduleState`) and never appear as a
 * stored availability value. See `presence-derivations.ts` (#3280).
 */
export type TesterAvailability = 'online' | 'away' | 'offline' | 'on_break' | 'invisible';

/**
 * Schedule type flag used by debit-math + break-allowance policy (#3755 data layer).
 *
 * - `part_time` (default) — 30 min/day break allowance.
 * - `full_time`           — 60 min/day break allowance.
 *
 * Persisted on `tester_profiles.schedule_type`. Default at the schema level
 * matches the existing "30-min lunch" assumption so legacy rows mean what
 * they always meant.
 */
export type TesterScheduleType = 'full_time' | 'part_time';

/**
 * Daily paid break-minute allowance per schedule type (#3756 accounting,
 * budget model #5880).
 *
 * Breaks chronologically debit against this allowance regardless of any single
 * break's length. Time within the allowance is billable (`breakPaidMinutes`);
 * excess spills into `breakUnpaidMinutes` and is deducted from net billable.
 *
 * Values match the long-standing "PT 30 / FT 60" convention documented
 * on `TesterScheduleType`.
 */
export const TESTER_SCHEDULE_TYPE_DAILY_BREAK_ALLOWANCE_MINUTES: Readonly<
  Record<TesterScheduleType, number>
> = {
  part_time: 30,
  full_time: 60,
};

/**
 * Human-facing label for a tester's schedule type — the single source of the
 * "Part-time" / "Full-time" copy across the timesheet badge and the profile
 * modal's break-allowance line (#4864).
 */
export const TESTER_SCHEDULE_TYPE_LABEL: Readonly<
  Record<TesterScheduleType, string>
> = {
  part_time: 'Part-time',
  full_time: 'Full-time',
};

/**
 * Default inactivity threshold: an `online` tester auto-promotes to `away`
 * after this many minutes of no mutating API interactions.
 *
 * Used only as the Zod default for `env.TESTER_AUTO_AWAY_MINUTES` on the server.
 * Clients MUST fetch the effective runtime value from GET /api/tester/config.
 */
export const TESTER_AUTO_AWAY_MINUTES_DEFAULT = 30;

/**
 * Default offline threshold: any non-offline tester auto-demotes to `offline`
 * after this many minutes of no mutating API interactions. (e.g. an `away`
 * tester finally gets dropped to `offline`.)
 *
 * Used only as the Zod default for `env.TESTER_AUTO_OFFLINE_MINUTES` on the server.
 * Clients MUST fetch the effective runtime value from GET /api/tester/config.
 */
export const TESTER_AUTO_OFFLINE_MINUTES_DEFAULT = 120;

/**
 * Default inactive-tester threshold: testers whose `lastActiveAt` is older than
 * this are surfaced under the "Inactive" presence section (derived state — not
 * stored).
 *
 * Used only as the Zod default for `env.TESTER_INACTIVE_DAYS` on the server.
 * Clients MUST fetch the effective runtime value from GET /api/tester/config.
 */
export const TESTER_INACTIVE_DAYS_DEFAULT = 10;

/**
 * Default warning threshold: toast + browser notification shown when this many
 * minutes remain before the auto-Away transition.
 *
 * Used only as the Zod default for `env.TESTER_INACTIVITY_WARNING_MINUTES` on the server.
 * Clients MUST fetch the effective runtime value from GET /api/tester/config.
 */
export const TESTER_INACTIVITY_WARNING_MINUTES_DEFAULT = 5;

/**
 * Tester performance metrics (cached for performance)
 */
export interface TesterMetrics {
  readonly totalJobsCompleted: number;
  /**
   * Average of the admin-authored internal review star ratings (1–5) for this
   * tester, or `null` when they have no reviews yet (#4951). This is the
   * "Performance Rating" surfaced on the internal/admin profile surfaces —
   * distinct from the customer thumbs-up/down approval signal it replaced.
   */
  readonly performanceRating: number | null;
  /** Count of admin internal reviews backing `performanceRating` (#4951). */
  readonly reviewCount: number;
  /**
   * Lifetime total of AI-extracted issues across every completed job.
   * Drives the "Issues found" tile on the public tester profile's
   * at-a-glance card. Computed via `jsonb_array_length(extracted_issues)`
   * server-side and summed across the tester's completed jobs.
   */
  readonly totalIssuesFound: number;
  /**
   * Lifetime total of AI-extracted feedback / suggestions across every
   * completed job. Same derivation as `totalIssuesFound` but against
   * `extracted_feedback`.
   */
  readonly totalSuggestionsMade: number;
}

/**
 * Full tester profile entity
 */
export interface TesterProfile {
  readonly id: string;
  readonly userId: string | null;
  readonly email: string | null;
  readonly alias: string;
  readonly avatarUrl: string | null;
  readonly bio: TesterBio;
  readonly status: TesterStatus;
  readonly availability: TesterAvailability;
  readonly scheduleType: TesterScheduleType;
  readonly devices: TesterDevice[];
  readonly languages: TesterLanguage[];
  readonly canCreateSocialVideos: boolean;
  readonly canSideloadApps: boolean;
  readonly slackUserId: string | null;
  /**
   * Tester's persisted local timezone (#4862). A valid IANA zone string
   * (`ScheduleTimezone`), or `null` when never set — distinct from the
   * browser-only `schedule-timezone` render preference (#4358), which is
   * never written here. The canonical value ops reads and the downstream
   * break-allowance work keys its 24h window off.
   */
  readonly timezone: ScheduleTimezone | null;
  readonly metrics: TesterMetrics;
  readonly lastActiveAt: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * Admin-directory row shape (#4609). Extends {@link TesterProfile} with
 * two computed, admin-only fields — the human-readable Discord/Slack
 * handles resolved by joining through `userId` to the `users` table.
 *
 * Kept out of `TesterProfile` itself because that type also represents
 * the row as stored on disk; if these fields lived on it, any code that
 * spreads a TesterProfile back into an `adapter.set(...)` upsert would
 * send a `discord_handle` / `slack_handle` column to Postgres, which
 * doesn't exist on `tester_profiles` (the data lives on the joined
 * `users` row).
 *
 * Only the `/api/admin/testers` list endpoint returns this shape.
 */
export interface AdminTesterListItem extends TesterProfile {
  /**
   * Linked Discord global handle (e.g. `edmund`) cached on `users.discord_username`.
   * `null` when the tester has not linked Discord or has no linked user account.
   */
  readonly discordHandle: string | null;
  /**
   * Linked Slack display name (resolved at admin-set time from `users.info`).
   * `null` when the tester has no Slack ID set or has no linked user account.
   */
  readonly slackHandle: string | null;
}

/**
 * Data required to create a new tester profile
 */
export interface CreateTesterProfileData {
  readonly userId?: string | null;
  readonly email?: string | null;
  readonly alias: string;
  readonly avatarUrl?: string | null;
  readonly bio: TesterBio;
  readonly devices?: TesterDevice[];
  readonly languages?: TesterLanguage[];
  readonly canCreateSocialVideos?: boolean;
  readonly canSideloadApps?: boolean;
  readonly slackUserId?: string | null;
  readonly status?: TesterStatus;
  readonly timezone?: ScheduleTimezone | null;
}

/**
 * Fields that can be updated on a tester profile (by the tester themselves)
 */
export interface UpdateTesterProfileData {
  readonly alias?: string;
  readonly avatarUrl?: string | null;
  readonly bio?: Partial<TesterBio>;
  readonly availability?: TesterAvailability;
  readonly devices?: TesterDevice[];
  readonly languages?: TesterLanguage[];
  readonly canCreateSocialVideos?: boolean;
  readonly canSideloadApps?: boolean;
  readonly timezone?: ScheduleTimezone | null;
}

/**
 * Fields that can be updated by admins.
 *
 * `scheduleType` is admin-only (#3755 data layer) — the tester themselves
 * can't change their PT/FT classification; the admin tester table is the
 * only set/unset surface, and `scheduleType` flows downstream into the
 * break-allowance debit math.
 */
export interface AdminUpdateTesterProfileData extends UpdateTesterProfileData {
  readonly status?: TesterStatus;
  readonly scheduleType?: TesterScheduleType;
}

/**
 * Tester profile response for API (public view)
 */
export interface TesterProfileResponse {
  readonly id: string;
  readonly alias: string;
  readonly avatarUrl: string | null;
  readonly bio: TesterBio;
  readonly availability: TesterAvailability;
  readonly scheduleType: TesterScheduleType;
  readonly devices: TesterDevice[];
  readonly languages: TesterLanguage[];
  readonly canCreateSocialVideos: boolean;
  readonly canSideloadApps: boolean;
  readonly timezone: ScheduleTimezone | null;
  readonly metrics: TesterMetrics;
  readonly lastActiveAt: string;
  readonly createdAt: string;
}

/**
 * Tester profile with recent jobs (extended response)
 */
export interface TesterProfileWithJobs extends TesterProfileResponse {
  readonly recentJobs: Array<{
    readonly id: string;
    readonly status: string;
    readonly completedAt: string | null;
    readonly testDurationSeconds: number | null;
  }>;
}

/**
 * Request body for applying to become a tester
 */
export interface ApplyToBeTesterRequest {
  alias: string;
  avatarUrl?: string;
  bio: {
    favoriteIceCream?: string;
    favoriteMovies?: string[];
    favoriteApps?: string[];
    hobbies?: string[];
    funFact?: string;
  };
  devices?: TesterDevice[];
  languages?: TesterLanguage[];
  canCreateSocialVideos?: boolean;
  canSideloadApps?: boolean;
}

/**
 * Tester pool requirements that customers can specify on a job.
 * The system enforces these against tester capabilities on claim.
 */
export interface TesterPoolRequirements {
  readonly requiredDevices?: TesterDevice[];
  readonly requiredLanguages?: TesterLanguage[];
  readonly requireSocialVideos?: boolean;
  readonly requireSideload?: boolean;
  /**
   * Identity targeting (#5743): when set, ONLY testers whose profile id is in
   * this list are eligible to claim — a "pool of one" sends a job to a specific
   * human. Set exclusively via the platform service-key job-creation endpoint;
   * the public job API never exposes it (reaching across the global pool to pin
   * a named tester is a platform-key-only capability).
   */
  readonly requiredTesterProfileIds?: readonly string[];
}

/**
 * Result of checking a tester's capabilities against job pool requirements.
 */
export interface PoolRequirementsCheckResult {
  readonly eligible: boolean;
  readonly missingRequirements: string[];
}

/**
 * Request body for updating tester profile
 */
export interface UpdateTesterProfileRequest {
  alias?: string;
  avatarUrl?: string | null;
  bio?: Partial<TesterBio>;
  availability?: TesterAvailability;
  devices?: TesterDevice[];
  languages?: TesterLanguage[];
  canCreateSocialVideos?: boolean;
  canSideloadApps?: boolean;
  timezone?: ScheduleTimezone | null;
}

// =============================================================================
// Tester Portal API Response Types
// =============================================================================

/**
 * Job listing for the tester portal
 *
 * Used by: GET /jobs (tester view)
 */
export interface TesterJob {
  readonly id: string;
  readonly status: import('../job/index.js').JobStatus;
  readonly modifier?: import('../job/index.js').JobModifier;
  readonly url: string;
  readonly description?: string;
  readonly targetDurationMinutes?: number;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly testerAlias?: string;
  readonly testerAvatarUrl?: string | null;
  readonly costUsd?: number;
  readonly testDurationSeconds?: number | null;
  readonly completedAt?: string | null;
  readonly claimedAt?: string | null;
}

/**
 * Paginated response for tester job listings
 */
export interface TesterJobsResponse {
  readonly items: readonly TesterJob[];
  readonly pagination: {
    readonly total: number;
    readonly limit: number;
    readonly offset: number;
    readonly hasMore: boolean;
  };
}

/**
 * The surface from which a tester claimed a job.
 *
 * Two of these — `slack` and `discord-activity` — are channel-shaped: the
 * tester expresses claim intent inside the channel (typing `ready` in Slack,
 * clicking the claim button in Discord Activity) but the actual test executes
 * in the web testing harness on desktop. They share the desktop bucket with
 * `tester-portal` because all three end up driving the same browser-based
 * testing flow. The mobile app is the opposite — the only surface that wires
 * up screen-share + mic capture on a mobile device.
 *
 * Surface membership is enforced both at claim time and at execution time
 * (resume / late-submission / write endpoints); see
 * `packages/shared/src/job/claim-surface.ts` for the rules and
 * `tester-auth-helpers.ts` in the api package for the execution-time gate.
 */
export const CLAIM_SOURCES = ['tester-portal', 'mobile-app', 'slack', 'discord-activity'] as const;
export type ClaimSource = (typeof CLAIM_SOURCES)[number];

/** Subset of `ClaimSource` values that hand the tester off to a desktop testing flow. */
export const DESKTOP_CLAIM_SOURCES: ReadonlySet<ClaimSource> = new Set([
  'tester-portal',
  'slack',
  'discord-activity',
]);

/** Subset of `ClaimSource` values that handle the testing flow on a mobile device. */
export const MOBILE_CLAIM_SOURCES: ReadonlySet<ClaimSource> = new Set(['mobile-app']);

/**
 * The `ClaimSource` values that the Queue Filter renders as chips (#4237).
 *
 * Distinct from `CLAIM_SOURCES`: that tuple is the analytics dimension and
 * still carries every place a claim can originate — Slack and Discord Activity
 * continue to record themselves on `jobs.claim_source` so reporting can
 * distinguish channel-shaped claims from web-portal ones. But as chips, Slack
 * and Discord were just labels on the same desktop bucket the `tester-portal`
 * chip already covers; selecting one had no effect over selecting the other.
 * We render only one representative per bucket here.
 *
 * Order is `[desktop, mobile]` so the chip row reads the same way as the
 * mobile app's binary surface toggle.
 */
export const CLAIM_SURFACE_FILTER_CHIPS = ['tester-portal', 'mobile-app'] as const satisfies
  readonly ClaimSource[];

/**
 * Display labels for `ClaimSource` values, used in surface chips and
 * dialog copy. Kept here next to the runtime tuple so any new surface
 * added must update both at once.
 */
export const CLAIM_SOURCE_LABELS: Readonly<Record<ClaimSource, string>> = {
  'tester-portal': 'Desktop',
  'mobile-app': 'Mobile',
  'slack': 'Slack',
  'discord-activity': 'Discord',
};

/**
 * Request body for POST /jobs/:jobId/claim
 */
export interface ClaimJobRequest {
  readonly claimSource: ClaimSource;
}

/**
 * Response from POST /jobs/:jobId/claim
 */
export interface ClaimJobResponse {
  readonly message: string;
  readonly job: {
    readonly id: string;
    readonly url: string;
    readonly description?: string;
    readonly targetDurationMinutes?: number;
    readonly status: string;
    readonly claimedAt: string;
  };
  readonly webTestLink: string;
}

/**
 * Response from GET /tester/profile/avatar-upload-url
 */
export interface AvatarUploadUrlResponse {
  readonly uploadUrl: string;
  readonly path: string;
  readonly publicUrl: string;
}

/**
 * Public tester profile shown to anyone who knows the tester id.
 *
 * Intentionally excludes per-job history — that lives behind
 * `GET /testers/:testerId/recent-jobs` which requires admin or self auth
 * (#3285) so customer URLs the tester worked on don't leak to the internet.
 *
 * Used by: GET /testers/:testerId/profile
 */
export interface PublicTesterProfile {
  readonly id: string;
  readonly alias: string;
  readonly avatarUrl: string | null;
  readonly bio: TesterBio;
  readonly availability: TesterAvailability;
  /**
   * Account-state status — `active` for the vast majority of viewers
   * (non-active testers 404 to anonymous + non-admin callers). Admin
   * viewers see `pending` / `rejected` / `deactivated` / `banned` so
   * dialog + profile-page surfaces can render status-aware copy (e.g.
   * "Deactivated" in the availability slot, "no upcoming shifts" in the
   * schedule strip). (#4362)
   */
  readonly status: TesterStatus;
  readonly devices: readonly TesterDevice[];
  readonly languages: readonly TesterLanguage[];
  /**
   * Tester's persisted local timezone (#4862), or `null` when never set.
   * Shown read-only on the profile modal + full profile page so ops can see
   * a tester's local time without entering edit mode.
   */
  readonly timezone: ScheduleTimezone | null;
  /** True if the underlying user account is a Runhuman admin. Powers the admin badge in the dialog header. */
  readonly isAdmin: boolean;
  /** True if the underlying user account is a Floor Manager. Powers the floor-manager badge in the dialog header. */
  readonly isFloorManager: boolean;
  readonly metrics: TesterMetrics;
  readonly createdAt: string;
  readonly lastActiveAt: string;
  /**
   * Tester's primary email. **Admin-viewers only** — populated when the
   * request carries a JWT belonging to a Runhuman admin (#4257). Returned
   * `null` for anonymous and non-admin signed-in viewers so the public
   * surface keeps the same anti-leak posture it had before. Used by the
   * tester profile dialog + admin tester profile page to render the
   * email-to-copy chip next to the alias.
   */
  readonly email: string | null;
  /**
   * The user account id backing this tester profile. **Admin-viewers only**
   * — populated when the request carries a JWT belonging to a Runhuman
   * admin (#4276), `null` otherwise. The admin tester profile page maps
   * this to `AdminJobsTab`'s `pinnedTesterUserId` so the Jobs tab can
   * pre-filter the canonical admin job index to one tester without
   * round-tripping through the profile list. Profiles that aren't yet
   * linked to a user account (Slack-only seed rows) carry `null`.
   */
  readonly userId: string | null;
  /**
   * Tester's Discord global handle (e.g. `edmund`). **Admin-viewers only**
   * — same gate as `email` (#4299). Returned `null` for anonymous and
   * non-admin signed-in viewers, and also `null` when the tester has not
   * linked Discord. Powers the Discord copy-chip in the tester profile
   * header badge row.
   */
  readonly discordHandle: string | null;
  /**
   * Tester's Slack display name (resolved at admin-set time from
   * `users.info` — display_name → name → real_name). **Admin-viewers
   * only**, same gate as `email`/`discordHandle`. `null` for non-admin
   * viewers and when no Slack ID is set. Powers the Slack copy-chip in
   * the tester profile header badge row.
   */
  readonly slackHandle: string | null;
  /**
   * Tester's schedule type (part-time / full-time). **Admin-or-self only**
   * (#4864) — populated when the caller is a Runhuman admin OR is the tester
   * viewing their own profile, `null` for a peer tester or anonymous viewer.
   * The break allowance is private, so this follows a stricter parity than the
   * admin-only `email`/`userId` fields: the tester themselves may also see it.
   * Powers the break-allowance line in the profile modal's Today/Schedule
   * section.
   */
  readonly scheduleType: TesterScheduleType | null;
  /**
   * Daily paid break-minute allowance derived from {@link scheduleType}
   * via `TESTER_SCHEDULE_TYPE_DAILY_BREAK_ALLOWANCE_MINUTES`. Same admin-or-self
   * gate as `scheduleType` (#4864) — `null` whenever `scheduleType` is `null`.
   */
  readonly dailyAllowanceMinutes: number | null;
}

/**
 * A single entry in a tester's recent-jobs history.
 *
 * Privileged view — the per-job `url` and `description` reveal which customer
 * apps the tester has been testing, so this shape is only returned by the
 * authenticated `/recent-jobs` endpoint (admin or self).
 */
export interface TesterRecentJob {
  readonly id: string;
  readonly url: string;
  readonly description?: string;
  readonly status: JobStatus;
  /**
   * Outcome modifier that pairs with `status` (#3954). Drives the
   * neutral `ModifierChip` rendered next to the status badge so admins
   * see the full outcome inline ("Completed · Late submission",
   * "Cancelled · Cancelled by tester", …) without opening Job Details.
   */
  readonly modifier?: JobModifier;
  readonly completedAt: string | null;
  readonly testDurationSeconds: number | null;
  readonly userRating: 'thumbs_up' | 'thumbs_down' | null;
  /**
   * Null for demo/personal jobs that aren't bound to a customer project.
   * IDs carried alongside the names so the history-tab title row can route
   * org / project clicks back into the admin Org/Project detail dialogs.
   */
  readonly project: {
    readonly id: string;
    readonly name: string;
    readonly organizationId: string;
    readonly organizationName: string;
  } | null;
}

/**
 * Response from GET /testers/:testerId/recent-jobs
 */
export interface TesterRecentJobsResponse {
  readonly items: readonly TesterRecentJob[];
}

/**
 * A single job a tester worked on within a date range (#4273).
 *
 * Powers the admin Tester Profile Activity tab's week-view calendar:
 * `startedAt` positions the block vertically inside its day-lane,
 * `endedAt` (or "now" while still in flight) sets the block height,
 * and the summary fields drive the same chips the Overview tab uses.
 *
 * `startedAt` is sourced from `claimedAt` — every job in this response
 * has been claimed by definition (we filter by claimedAt being in range).
 * `endedAt` mirrors `completedAt`: non-null on terminal jobs, null on
 * still-in-flight ones so the renderer can paint an open-bottom block.
 */
export interface TesterJobInRange {
  readonly id: string;
  readonly url: string;
  readonly description?: string;
  readonly status: JobStatus;
  readonly modifier?: JobModifier;
  readonly startedAt: string;
  readonly endedAt: string | null;
  readonly testDurationSeconds: number | null;
  readonly userRating: 'thumbs_up' | 'thumbs_down' | null;
  /**
   * Null for demo/personal jobs that aren't bound to a customer project.
   * Same shape as `TesterRecentJob.project` so the Activity tab can reuse
   * the project-label rendering primitives.
   */
  readonly project: {
    readonly id: string;
    readonly name: string;
    readonly organizationId: string;
    readonly organizationName: string;
  } | null;
}

/**
 * One online-presence span on the Activity timeline: a window during which the
 * tester was clocked in (available or working), sourced from
 * `tester_online_periods`. `offlineAt` is `null` for the still-open period (the
 * tester is online right now) so the renderer can paint it up to "now".
 */
export interface TesterActivityOnlinePeriod {
  readonly onlineAt: string;
  readonly offlineAt: string | null;
  readonly offlineReason: 'user' | 'away_timeout' | 'hidden' | 'admin' | null;
}

/**
 * One scheduled-availability span on the Activity timeline: a window the tester
 * was *meant* to be online (recurring rules ⊕ ad-hoc add overrides, minus
 * remove overrides). Half-open `[startsAt, endsAt)` in UTC.
 */
export interface TesterActivityScheduledSegment {
  readonly startsAt: string;
  readonly endsAt: string;
}

/**
 * One break span on the timeline: an approved/proposed step-away window sourced
 * from `tester_step_away_events`. Half-open `[startsAt, endsAt)` in UTC. Only
 * live statuses (`pending` / `suggested` / `approved`) surface — declined and
 * cancelled breaks never happened, so they carry no activity meaning.
 */
export interface TesterActivityBreak {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: BreakRequestStatus;
  readonly reason: string | null;
}

/**
 * One absence span on the timeline: a tester time-off request sourced from the
 * `tester_schedule_overrides` `remove` rows (`metadata.source='time-off-request'`).
 * Half-open `[startsAt, endsAt)` in UTC. `declined` requests are excluded — the
 * tester was still expected to work — so only `pending` / `approved` surface.
 */
export interface TesterActivityAbsence {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  readonly status: TimeOffRequestStatus;
  readonly reason: string | null;
}

/**
 * Response from GET /admin/testers/:testerId/activity-timeline.
 *
 * The independent tracks the profile timelines stack per day-row. The Activity
 * tab stacks jobs / online / scheduled; the Schedule tab stacks observed
 * activity (online + breaks + absences) against scheduled work. Each field is
 * an absolute-time series the client projects onto local-tz day lanes:
 *
 *   - `jobs`              — jobs the tester worked (status-colored).
 *   - `onlinePeriods`     — spans the tester was actually clocked in.
 *   - `scheduledSegments` — spans the tester was scheduled to be online.
 *   - `breaks`            — step-away windows (observed/approved deviations).
 *   - `absences`          — time-off request windows (observed absences).
 */
export interface TesterActivityTimelineResponse {
  readonly jobs: readonly TesterJobInRange[];
  readonly onlinePeriods: readonly TesterActivityOnlinePeriod[];
  readonly scheduledSegments: readonly TesterActivityScheduledSegment[];
  readonly breaks: readonly TesterActivityBreak[];
  readonly absences: readonly TesterActivityAbsence[];
}

// =============================================================================
// Tester Presence Types (#2249, #2250, #2720)
// =============================================================================

/**
 * Schedule state for the current hour, computed from recurring + overrides.
 * - 'on-shift': Tester is scheduled now (recurring or ad-hoc override-add)
 * - 'off-schedule': Tester is not scheduled for this hour
 * - 'excused': Tester has a recurring slot but an override-remove (approved time off)
 */
export type TesterScheduleState = 'on-shift' | 'off-schedule' | 'excused';

/**
 * Active job summary shown in the presence bar when a tester is currently testing
 */
export interface TesterPresenceActiveJob {
  readonly jobId: string;
  readonly url: string;
  readonly description: string;
  readonly claimedAt: string;
  /** Null for demo/personal jobs that aren't bound to a customer project. */
  readonly project: {
    readonly name: string;
    readonly organizationName: string;
  } | null;
}

/**
 * A single tester's presence state as shown in the presence bar.
 *
 * `isFloorManager` reflects the underlying user account flag (#3642).
 * It surfaces here so every consumer of the presence list (web sidebar,
 * mobile team list, the admin schedule's Floor Manager card roster)
 * shares one source of truth and avoids hardcoded name lists.
 */
export interface TesterPresenceItem {
  readonly id: string;
  readonly alias: string;
  readonly avatarUrl: string | null;
  readonly availability: TesterAvailability;
  readonly activeJob: TesterPresenceActiveJob | null;
  readonly scheduleState: TesterScheduleState;
  readonly lastActiveAt: string;
  readonly isFloorManager: boolean;
  /**
   * Devices the tester has registered. Surfaces device coverage on
   * presence chips (Activity + web) without a follow-up profile fetch
   * per row. Empty array when the tester has registered no devices.
   * Refs #3968.
   */
  readonly devices: readonly TesterDevice[];
  /**
   * End-time of the tester's currently-active approved break, when one
   * exists. ISO timestamp; `null` for every tester without an approved
   * break covering "now" (or whose break is already returned / cancelled).
   *
   * Surfaced so the Discord Activity PresenceRoster can render a
   * "back at HH:MM" chip alongside the existing "On break" label.
   * The chip's gate is `availability === 'on_break' && onBreakUntil !== null`
   * — the field is decoupled from the persisted `availability` so the
   * gate stays simple even when the periodic sweep that writes
   * `availability='on_break'` lags behind the approved-break window.
   * Refs #4166.
   */
  readonly onBreakUntil: string | null;
}

/**
 * Response from GET /tester/presence
 */
export interface TesterPresenceResponse {
  readonly testers: readonly TesterPresenceItem[];
  readonly waitingJobCount: number;
}
